import React, { useState, useMemo, useEffect } from 'react'

import PresentationNavContextProvider from '../PresentationNav/PresentationNavContext/PresentationNavContextProvider'
import PresentationNavMobile from '../PresentationNav/PresentationNavMobile/PresentationNavMobile'

import type { PresentationBlock, PresentationOptionV2, PresentationPageV2, PresentationSectionV2 } from 'paintscout'

import blockComponents, {
  AboutEstimatorBlock,
  AwardsBlock,
  BannerBlock,
  ButtonBlock,
  CardsBlock,
  CustomBlock,
  DividerBlock,
  DocumentsBlock,
  GalleryBlock,
  ImageBlock,
  LogoBlock,
  MissionBlock,
  PicturesBlock,
  QuoteBlock,
  ServicesBlock,
  SliderImageBlock,
  SocialLinksBlock,
  SpacerBlock,
  TermsBlock,
  TestimonialsBlock,
  TextBlock,
  ThreeColumnBlock,
  TwoColumnBlock,
  WorkOrderBlock
} from '../blocks'

import AddBlockButton from '../util/AddBlockButton'
import SectionDividerMenu from '../util/SectionDividerMenu'
import BlockMenu from '../util/BlockMenu'
import {
  getBlockPadding,
  BlockOptionsPopup,
  loadPresentationFonts,
  applyPresentationDragResult,
  PRESENTATION_BLOCK_DND_TYPE,
  PRESENTATION_NAV_BLEED_LEFT_VAR,
  PRESENTATION_NAV_BLEED_RIGHT_VAR
} from '../util'
import IntroToEditorDialog from '../dialogs/IntroToEditorDialog'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import type { DropResult, DraggableProvided } from '@hello-pangea/dnd'
// import NavHelpDialog from '../dialogs/NavHelpDialog'

// import { PresentationNavMobile } from '../PresentationNav'

import { Grid, Typography, useMediaQuery, makeStyles, ClickAwayListener } from '@paintscout/ui'
import { getPresentationContentStylesNext } from '@paintscout/ui/styles'
import { useClientOptions, useDialogs } from '@paintscout/ui-business'
import { Placeholder } from '../components'

import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined'

import Quote from '../../Quote/Quote'
import QuoteTerms from '../../Quote/QuoteTerms'

import type { PresentationBlockProps } from '../'

import {
  createPresentationNextBlock,
  findBlock,
  findSection,
  getDefaultPresentationImages,
  getObjectLabels,
  getPresentationBlockOptions,
  getPresentationLogo,
  getQuoteNextPresentation,
  replaceSectionImages
} from '@paintscout/util/builder'

// import findIndex from 'lodash/findIndex'

import classnames from 'classnames'
import type { QuoteView } from '../../Quote'
import { useQuote, usePresentation } from '../../context'
import { useFormikContext } from 'formik'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'

import { useFrame } from 'react-frame-component'
import AcceptedBadge from '../util/AcceptedBadge'

import striptags from 'striptags'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'

const useStyles = makeStyles<Theme, any>(
  (theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      ...theme.typography.body1,
      ['@media print']: {
        display: 'none'
      }
    },
    printOnly: {
      opacity: 0,
      position: 'absolute',
      visibility: 'hidden',
      ['@media print']: {
        opacity: 1,
        position: 'static',
        visibility: 'visible'
      }
    },
    // pageBreak: {
    //   display: 'block',
    //   content: '""',
    //   pageBreakAfter: 'always'
    // },
    content: (props) => ({
      ...props.presentationContentStyles,
      overflowWrap: 'break-word'
    }),
    pageBorder: {
      // height: 0,
      // position: 'relative',
      // zIndex: 2,
      // width: '100%',
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      color: theme.palette.secondary.main,
      '&:before, &:after': {
        content: '""',
        flex: 1,
        borderBottom: `1px dashed ${theme.palette.secondary.main}`
      },
      '&:before': {
        margin: '0 20px 0 -20px'
      },
      '&:after': {
        margin: '0 -20px 0 20px'
      }
    },
    addBlockButton: {
      display: 'none'
    },
    showAddBlockButton: {
      display: 'flex'
    },
    sectionWrapper: {
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
      position: 'relative',
      borderCollapse: 'separate'
    },
    hideSectionOverflow: {
      overflow: 'hidden'
    },
    pointer: {
      cursor: 'pointer'
    },
    backgroundWrapper: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%'
    },
    widths: {
      marginLeft: 'auto',
      marginRight: 'auto',
      boxSizing: 'border-box',
      width: 1080,
      maxWidth: '100%',
      ['@media print']: {
        width: 'auto'
      }
    },
    widthsResponsive: {
      [theme.breakpoints.down('md')]: {
        width: 718
      },
      [theme.breakpoints.down('sm')]: {
        width: '96%'
      }
    },
    quoteSectionBorder: {
      border: `1px solid ${theme.palette.grey[800]}`,
      borderRadius: theme.borderRadius.md,
      margin: theme.spacing(2, 0),
      padding: theme.spacing(6, 6),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(4, 2)
      }
    },
    section: {
      position: 'relative',
      '&:hover': {
        '& $addBlockButton': {
          display: 'flex'
        },
        '& > $sectionWrapperHovered': {
          '&::before': {
            display: 'block'
          }
        },
        '& .sectionHoverMenu': {
          display: 'flex'
        }
      }
    },
    sectionWrapperHovered: {
      '&::before': {
        zIndex: 1,
        display: 'none',
        content: '""',
        position: 'absolute',
        left: -theme.spacing(3) + 2,
        right: -theme.spacing(3) + 2,
        top: -2,
        bottom: -2,
        border: `2px solid ${theme.palette.presentation.sectionBorder.hover}`,
        pointerEvents: 'none'
        //  borderRadius: theme.borderRadius['md'],
      }
    },
    focusBorder: {
      zIndex: 2,
      '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        left: -theme.spacing(3) + 2,
        right: -theme.spacing(3) + 2,
        top: -2,
        bottom: -2,
        // no background fill — StickyBid painted this white, but PaintScout's editor
        // canvas is grey and the fill showed as white slabs in the side gutters
        border: `2px solid ${theme.palette.presentation.sectionBorder.focus}`,
        zIndex: -1
        //  borderRadius: theme.borderRadius['md'],
      }
    },
    newPagePadding: {
      paddingBottom: 0
    },
    dummyNav: {
      // cursor: 'pointer',
      position: 'relative',
      // #22068: the mock nav should read as a full-width page header like the real (fixed) customer
      // nav, not as a box bounded by the hamburger / estimate buttons. Bleed it back out of the 1080
      // canvas column so it spans the whole editor canvas. The gutters are measured & published by
      // PresentationEdit; everywhere else (settings dialogs, storybook) they default to 0px and the
      // nav stays exactly as wide as the column.
      marginLeft: `calc(-1 * var(${PRESENTATION_NAV_BLEED_LEFT_VAR}, 0px))`,
      marginRight: `calc(-1 * var(${PRESENTATION_NAV_BLEED_RIGHT_VAR}, 0px))`,
      '&:hover': {
        '& $dummyNavText': {
          opacity: 1
        }
      }
    },
    dummyNavText: {
      position: 'absolute',
      zIndex: 3,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
      opacity: 0,
      transition: 'opacity 350ms ease'
    },
    dummyNavToolbar: {
      // pad the toolbar back in by the same gutters, so its content box still lines up with the
      // canvas column and neither the hamburger nor the estimate button moves as the bar widens.
      // maxWidth has to be released too: PresentationNavMobile clamps the toolbar to 1144 and centers
      // it, which would re-center the buttons inside the (asymmetrically) widened bar.
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: 'none',
      padding: 0,
      paddingLeft: `var(${PRESENTATION_NAV_BLEED_LEFT_VAR}, 0px)`,
      paddingRight: `var(${PRESENTATION_NAV_BLEED_RIGHT_VAR}, 0px)`
    }
  }),
  { name: 'Presentation' }
)

export interface PresentationProps {
  classes?: StyleClasses<typeof useStyles>
  presentation?: PresentationOptionV2
  page?: string
  isPreview?: boolean
  isScreenshot?: boolean
  editFromDialog?: boolean
  isSettings?: boolean
  presentationTheme?: Theme
  loadAllFonts?: boolean

  getHeight?: (height: number) => void
  onPresentationClick?: (key: string) => void

  /**
   * Editing callbacks. All address nodes by **key**, not by index path — the pre-#21990 versions
   * took `(sectionIndex, pageIndex)` pairs, which silently target the wrong node when the caller's
   * indices are stale (anything reordered or deleted between render and click). Implementations
   * delegate to the tree helpers in @paintscout/util, which no-op on an unresolved key.
   */
  handleCreateSection?: (newSection: PresentationSectionV2, args: { pageKey: string; index?: number }) => void
  handleCreateBlock?: (newBlock: PresentationBlock, args: { sectionKey: string; index?: number }) => void
  handleEditSection?: (section: PresentationSectionV2) => void
  handleEditBlock?: (block: PresentationBlock, isDirty?: boolean) => void
  handleDeleteBlock?: (blockKey: string) => void
  /** Whole-tree replace, used by drag-and-drop where several nodes shift at once. */
  handlePagesChange?: (pages: PresentationPageV2[]) => void
  showHelp?: boolean
  filterTypes?: string[]
  view?: QuoteView
  hidePreviewFrame?: boolean
}

function Presentation({
  editFromDialog,
  getHeight,
  handleCreateSection,
  handleCreateBlock,
  handleDeleteBlock,
  handlePagesChange,
  isScreenshot,
  isSettings = true,
  loadAllFonts,
  presentationTheme: presentationThemeProp,
  showHelp,
  view,
  ...props
}: PresentationProps) {
  const {
    isEditable,
    preview,
    focusedBlock,
    setFocusedBlock,
    presentationTheme: presentationThemeHook,
    addPages
  } = usePresentation()

  const presentationTheme = presentationThemeProp ?? presentationThemeHook
  const { setFieldValue, submitForm } = useFormikContext<PresentationOptionV2>()
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const { dismissAllDialogs, openDialog, stack: dialogStack } = useDialogs()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { quote, estimator } = useQuote()
  const presentation =
    props.presentation ??
    getQuoteNextPresentation({
      options,
      quote,
      current: true,
      isWorkOrder: view === 'work-order',
      isProductOrderForm: view === 'product-order-form'
    })

  let presentationContentStyles = presentationTheme
    ? getPresentationContentStylesNext(presentationTheme, { noMargins: ['li'] })
    : {}
  useMemo(() => {
    presentationContentStyles = getPresentationContentStylesNext(presentationTheme, { noMargins: ['li'] })
  }, [presentationTheme])
  const classes = useStyles({ ...props, presentationContentStyles })

  const { window: win, document: doc } = useFrame()

  useEffect(() => {
    if (isSettings || loadAllFonts) {
      // load all google fonts
      loadPresentationFonts({ win, doc })
    } else {
      // if not making a presentation load only the google fonts we need to display it
      const fonts = []
      const style = presentation.advanced.style
      if (!style) return
      fonts.push(style.fontFamily)
      const typogs = ['body', 'heading1', 'heading2', 'heading3']
      typogs.forEach((typog) => {
        const family = style[typog]?.fontFamily
        if (family && fonts.indexOf(family) === -1) fonts.push(family)
      })
      const fontsToLoad = fonts.filter(
        (font) => !['default', 'degular, sans-serif', 'georgia, serif', 'roboto, sans-serif'].includes(font)
      )
      loadPresentationFonts({ win, doc, fontFamilies: fontsToLoad })
    }
  }, [isSettings, loadAllFonts, presentation.advanced?.style, win, doc])

  useEffect(() => {
    // give invoice presentations a background of theme.palette.grey['100']
    if (!isSettings && quote.is_invoice) {
      doc.querySelector('body').style.background = '#f5f5f5'
    }

    return () => {
      doc.querySelector('body').style.removeProperty('background')
    }
  }, [isSettings, quote.is_invoice, doc])

  const location = useLocation()
  const { isNew } = qs.parse(location.search) as {
    isNew: string
  }

  useEffect(() => {
    if (isSettings && window.localStorage.getItem('isIntroToEditorDialogDismissed') !== 'true' && isNew) {
      openDialog(IntroToEditorDialog, {
        onCancel: dismissAllDialogs
      })
    }
  }, [isSettings, isNew, openDialog, dismissAllDialogs])

  const handleClickOn = (ev: React.MouseEvent<Element, MouseEvent>, sectionKey: string) => {
    if (isSettings && focusedBlock !== sectionKey) {
      setFocusedBlock(sectionKey)
      setMenuAnchorEl(null)
    }
  }

  const handleClickAway = (ev: React.MouseEvent<Document, MouseEvent>, sectionKey: string) => {
    if (focusedBlock === sectionKey) {
      ev.stopPropagation()
      submitForm()
      setFocusedBlock('')
      setMenuAnchorEl(null)
    }
  }

  const handleDragEnd = (result: DropResult) => {
    // droppableIds are container keys (a section key for a block drag, a page key for a section
    // drag), so the whole translation lives in applyPresentationDragResult — see ../util/dragBlocks
    const updated = applyPresentationDragResult(presentation, result)

    if (updated) {
      handlePagesChange(updated.pages)
    }
  }

  // const filterTypes = props?.filterTypes ?? null

  const isPreview = props.isPreview ?? (!!preview && preview !== 'none')
  const editable = !isPreview && isSettings
  const dragEnabled = editable && isEditable && !!handlePagesChange
  const isPrint = useMediaQuery('print')
  // get size from useMediaQuery
  let size: 'desktop' | 'tablet' | 'mobile' = 'desktop'
  // fixed values (StickyBid's effective defaults) rather than theme-relative
  // 'md'/'sm': the ambient theme differs between the app (md=958/lg=1214) and
  // customer-view (md=768/lg=1024), which made an iPad landscape (1024px)
  // resolve as 'desktop' and strip the text inset entirely
  const isTablet = useMediaQuery(1280)
  const isMobile = useMediaQuery(960)
  if (isTablet) size = 'tablet'
  if (isMobile) size = 'mobile'

  // const stacked = presentation?.advanced?.infiniteScroll
  // const stacked = true
  const presentationStyle = presentation.advanced.style
  const { palette } = presentationStyle || {}
  const presetColors = palette?.title ? Object.values(palette.colors) : undefined

  if (getHeight) {
    const presentationEl = doc.getElementById('presentation')
    if (presentationEl) {
      getHeight(presentationEl.offsetHeight)
    }
  }

  // const pageIndex = getPageIndex(page)
  // const groupedSections: PresentationBlock[][] = []
  // const pageIds: string[] = []
  // If we're using the stacked layout, we want ALL the sections from ALL
  // the pages. Otherwise, just the sections from the active page.
  // if (stacked) {
  // presentation.pages.forEach((page) => {
  //   groupedSections.push(
  //     page.sections.filter(filterAllowedTypes).map(function (section, index) {
  //       return section
  //     })
  //   )
  //   pageIds.push(page.key)
  // })
  // } else {
  // pages.push(presentation.pages[pageIndex].sections.filter(filterAllowedTypes))
  // }
  /**
   * Which page the canvas is showing. A page renders on its own — pages are never stacked (#21990).
   *
   * `props.page` is whatever the nav or the sidebar tree last selected, and that can be a key at any
   * level: a page (nav tab), a section (single-page nav, or a tree row), or a block (tree row). So
   * resolve it to its owning page rather than assuming the level, and fall back to the first page
   * when the key is stale or absent.
   */
  const activePage = useMemo<PresentationPageV2>(() => {
    const pages = presentation.pages ?? []
    const selectedKey = props.page

    if (!selectedKey) {
      return pages[0]
    }

    return (
      pages.find((candidate) => candidate.key === selectedKey) ??
      findSection(presentation, selectedKey)?.page ??
      findBlock(presentation, selectedKey)?.page ??
      pages[0]
    )
  }, [presentation, props.page])

  // blockPrefix (the formik path handed to each block) is absolute from the presentation root, so
  // it needs the active page's position, not just the section's
  const activePageIndex = Math.max(
    0,
    (presentation.pages ?? []).findIndex((candidate) => candidate.key === activePage?.key)
  )

  const hasTerms = !!striptags(quote.terms?.content)

  return (
    <>
      {!isScreenshot && !editable && (
        <div className={classes.printOnly}>
          <Quote {...({ inPresentation: true, printOnly: true } as any)} />
          {hasTerms && (
            <>
              <div className={classes.pageBreak}></div>
              <QuoteTerms />
            </>
          )}
        </div>
      )}
      <AcceptedBadge quote={quote} />
      <div className={classes.root} id={'presentation'}>
        {editable && (
          <PresentationNavContextProvider
            allowResponse={false}
            presentation={presentation}
            view={view}
            isPreview={isPreview}
            isDummy
            isCustomerView
          >
            <div
              className={classes.dummyNav}
              // onClick={() => {
              //   openDialog(NavHelpDialog, {
              //     onCancel: dismissDialog
              //   })
              // }}
            >
              <Typography variant={'overline'} className={classes.dummyNavText}>
                See Preview to Try Navigation
              </Typography>
              <PresentationNavMobile classes={{ toolbar: classes.dummyNavToolbar }} />
            </div>
          </PresentationNavContextProvider>
        )}
        {(() => {
          const sectionsContent = (activePage?.sections ?? []).map((section, sectionIndex) => {
            if (section.blocks.length === 0 && isSettings) {
              // empty section
              const emptySectionPlaceholder = (
                <div className={classnames(classes.padding, classes.newPagePadding)}>
                  <Placeholder
                    Icon={InsertDriveFileOutlinedIcon}
                    buttonText={`Add Block`}
                    onClick={() => document.getElementById('add-section-bottom-new-page').click()}
                  />
                </div>
              )
              return (
                <div id={section.key} key={section.key}>
                  {editable && (
                    <SectionDividerMenu
                      index={sectionIndex}
                      pageKey={activePage.key}
                      tooltipTitle={'Add / Edit Section'}
                    />
                  )}
                  {dragEnabled ? (
                    <Droppable droppableId={section.key} type={PRESENTATION_BLOCK_DND_TYPE}>
                      {(dropProvided) => (
                        <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                          {emptySectionPlaceholder}
                          {dropProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ) : (
                    emptySectionPlaceholder
                  )}
                  <AddBlockButton
                    key={`add-section-bottom-new-page`}
                    id={'add-section-bottom-new-page'}
                    addPages={addPages}
                    objectLabels={objectLabels}
                    onActionClick={(ev, actionKey) => handleAddBlock(actionKey, { sectionKey: section.key, index: 0 })}
                    onAddSection={(newSection: PresentationSectionV2) =>
                      handleCreateSection(newSection, { pageKey: activePage.key, index: sectionIndex })
                    }
                    presentation={presentation}
                  />
                </div>
              )
            } else {
              // page with section(s)
              const blocksContent = section.blocks
                .filter((block) => {
                  const isEmpty = blockComponents?.[block.type]?.isEmpty
                  if (!isEmpty) {
                    return false
                  }

                  if (!isSettings && isEmpty({ quote, options, block, isPrint, isSettings, estimator })) {
                    return false
                  }

                  return true
                })
                .map((block, blockIndex) => {
                  // const SectionComponent = blockComponents?.[block.type]?.Section
                  // const useCart = presentation.advanced?.useCart
                  const blockCount = section.blocks.length
                  // quote block isn't rendered if !!useCart so we discount it from top/bottom
                  // const isTopBlock = index === 0 || (index === 1 && section.blocks[0].type === 'quote' && useCart)
                  // const isBottomBlock =
                  // index === blockCount - 1 ||
                  // (index === blockCount - 2 && section.blocks[blockCount - 1].type === 'quote' && useCart)
                  // quote button is back in for now
                  const isTopBlock = blockIndex === 0
                  const isBottomBlock = blockIndex === blockCount - 1

                  const blockContent = (dragProvided?: DraggableProvided) => (
                    <section
                      key={block.key}
                      ref={dragProvided?.innerRef}
                      {...(dragProvided?.draggableProps ?? {})}
                      {...(dragProvided?.dragHandleProps ?? {})}
                    >
                      <ClickAwayListener
                        onClickAway={(ev) => handleClickAway(ev, block.key)}
                        {...(dialogStack.length && { mouseEvent: false, touchEvent: false })}
                      >
                        <div className={classes.block} onClick={(ev) => handleClickOn(ev, block.key)}>
                          {isSettings && isEditable && (!addPages || (addPages && isTopBlock)) && (
                            <AddBlockButton
                              key={`add-block-top-block-${block.key}`}
                              objectLabels={objectLabels}
                              focused={focusedBlock === block.key}
                              classes={{
                                root: classnames({
                                  [classes.addBlockButton]: true,
                                  [classes.showAddBlockButton]: focusedBlock === block.key
                                })
                              }}
                              onActionClick={(ev, actionKey) =>
                                handleAddBlock(actionKey, { sectionKey: section.key, index: blockIndex })
                              }
                              addPages={addPages}
                              onAddSection={(newSection: PresentationSectionV2) =>
                                handleCreateSection(newSection, { pageKey: activePage.key, index: sectionIndex })
                              }
                              presentation={presentation}
                            />
                          )}
                          {getBlock({
                            type: block.type,
                            props: {
                              editFromDialog: editFromDialog,
                              focusedBlock: focusedBlock,
                              index: blockIndex,
                              isEditable: editable && focusedBlock === block.key,
                              isInvoice: quote.is_invoice,
                              isPreview: isPreview,
                              isSettings: isSettings,
                              objectLabels: objectLabels,
                              sectionIndex: sectionIndex,
                              presentationContentStyles: presentationContentStyles,
                              presentationIndustry: presentation.advanced.industry,
                              presetColors: presetColors,
                              block: block,
                              blockPrefix: `pages[${activePageIndex}].sections[${sectionIndex}].blocks[${blockIndex}]`,
                              setFieldValue: setFieldValue,
                              showHelp: showHelp,
                              size: size,
                              submitForm: submitForm,
                              useCart: presentation.advanced?.useCart,
                              WrapperComponent: BlockWrapper
                            },
                            logo: getPresentationLogo({ options, quote, presentation }).logo
                          })}
                          {isSettings && isEditable && (!addPages || (addPages && isBottomBlock)) && (
                            <AddBlockButton
                              key={`add-block-bottom-block-${block.key}`}
                              objectLabels={objectLabels}
                              focused={focusedBlock === block.key}
                              classes={{
                                root: classnames({
                                  [classes.addBlockButton]: true,
                                  [classes.showAddBlockButton]: focusedBlock === block.key
                                })
                              }}
                              addPages={addPages}
                              onActionClick={(ev, actionKey) =>
                                handleAddBlock(actionKey, { sectionKey: section.key, index: blockIndex + 1 })
                              }
                              onAddSection={(newSection: PresentationSectionV2) =>
                                handleCreateSection(newSection, { pageKey: activePage.key, index: sectionIndex + 1 })
                              }
                              presentation={presentation}
                            />
                          )}
                          {isSettings && !isPreview && (
                            <>
                              <BlockMenu
                                addPages={addPages}
                                focusedBlock={focusedBlock}
                                handleCreateBlock={handleCreateBlock}
                                handleDeleteBlock={handleDeleteBlock}
                                handlePagesChange={handlePagesChange}
                                index={blockIndex}
                                sectionKey={section.key}
                                presentation={presentation}
                                block={block}
                                sectionOptionsOpen={!!menuAnchorEl}
                                setFocusedBlock={setFocusedBlock}
                                setMenuAnchorEl={setMenuAnchorEl}
                              />
                              {focusedBlock === block.key && !!menuAnchorEl && (
                                <BlockOptionsPopup
                                  menuAnchorEl={menuAnchorEl}
                                  onClose={() => setMenuAnchorEl(null)}
                                  open
                                  blockKey={block.key}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </ClickAwayListener>
                    </section>
                  )

                  if (!dragEnabled) {
                    return blockContent()
                  }

                  return (
                    <Draggable draggableId={block.key} index={blockIndex} key={block.key}>
                      {(dragProvided) => blockContent(dragProvided)}
                    </Draggable>
                  )
                })

              return (
                <div id={section.key} key={section.key}>
                  {editable && (
                    <SectionDividerMenu
                      index={sectionIndex}
                      pageKey={activePage.key}
                      tooltipTitle={'Add / Edit Section'}
                    />
                  )}
                  {dragEnabled ? (
                    <Droppable droppableId={section.key} type={PRESENTATION_BLOCK_DND_TYPE}>
                      {(dropProvided) => (
                        <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                          {blocksContent}
                          {dropProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ) : (
                    blocksContent
                  )}
                </div>
              )
            }
          })

          return dragEnabled ? (
            <DragDropContext onDragEnd={handleDragEnd}>{sectionsContent}</DragDropContext>
          ) : (
            sectionsContent
          )
        })()}
        {editable && <SectionDividerMenu index={-1} pageKey={activePage?.key} tooltipTitle={'Add Section'} />}
      </div>
    </>
  )

  // function getPageIndex(page: string) {
  //   if (!page) {
  //     return 0
  //   }

  //   const i = findIndex(presentation.pages, { key: page })
  //   return i >= 0 ? i : 0
  // }

  /** Adds a block of `type` into `sectionKey`, at `index` (appended when omitted). */
  function handleAddBlock(type: string, args: { sectionKey: string; index?: number }) {
    const images = getDefaultPresentationImages({ options, presentation })
    const blockOptions = getPresentationBlockOptions({ options })
    const blockOption = blockOptions.find((option) => option.type === type)
    const newBlock = createPresentationNextBlock({ blockOption })
    // placeholder images in the catalog entry get swapped for the company's own
    const { section: newBlockWithImages } = replaceSectionImages({ section: newBlock, images })

    handleCreateBlock(newBlockWithImages, args)
    setFocusedBlock(newBlock.key)
  }

  // function filterAllowedTypes(section: PresentationBlock) {
  //   return filterTypes === null || filterTypes.includes(section?.type)
  // }
}

function getBlock(args: { type: string; props: PresentationBlockProps; logo: string }) {
  const { logo, props, type } = args

  // Sherwin-Williams partner content (#21311) — dispatched via the section registry
  // rather than one case per type (Benjamin Moore is stale/unused and stays v1-only)
  if (type.startsWith('sherwin-williams')) {
    const PartnerSection = blockComponents?.[type]?.Section
    return PartnerSection ? <PartnerSection {...props} /> : null
  }

  switch (type) {
    case 'logo':
      return <LogoBlock logo={logo} {...props} />
    case 'banner':
      return <BannerBlock {...props} />
    case 'button':
      return <ButtonBlock {...props} />
    case 'text':
      return <TextBlock {...props} />
    case 'image':
      return <ImageBlock {...props} />
    case '2-column':
      return <TwoColumnBlock {...props} />
    case '3-column':
      return <ThreeColumnBlock logo={logo} {...props} />
    case 'cards':
      return <CardsBlock {...props} />
    case 'gallery':
      return <GalleryBlock {...props} />
    case 'testimonials':
      return <TestimonialsBlock {...props} />
    case 'divider':
      return <DividerBlock {...props} />
    case 'social-links':
      return <SocialLinksBlock {...props} />
    case 'spacer':
      return <SpacerBlock {...props} />
    case 'quote':
      return <QuoteBlock {...props} />
    case 'terms':
      return <TermsBlock {...props} />
    case 'about-estimator':
      return <AboutEstimatorBlock {...props} />
    case 'awards':
      return <AwardsBlock {...props} />
    case 'documents':
      return <DocumentsBlock {...props} />
    case 'mission':
      return <MissionBlock {...props} />
    case 'services':
      return <ServicesBlock {...props} />
    case 'work-order':
      return <WorkOrderBlock {...props} />
    case 'custom':
      return <CustomBlock {...props} />
    case 'slider-image':
      return <SliderImageBlock {...props} />
    case 'pictures':
      return <PicturesBlock {...props} />
  }
}

export interface BlockWrapperProps extends PresentationBlockProps {
  children: React.ReactElement
}

const BlockWrapper = function ({
  children,
  index,
  block,
  isPreview,
  isInvoice,
  isSettings,
  sectionIndex,
  size,
  presentationContentStyles,
  focusedBlock,
  useCart,
  ...props
}: BlockWrapperProps) {
  const classes = useStyles({ ...props, presentationContentStyles })

  if (!block) {
    return null
  }

  const isQuoteSection = ['quote', 'work-order'].includes(block.type)
  const isEmbeddedQuote = isQuoteSection && !useCart
  const isInvoiceTerms = isInvoice && block.type === 'terms'
  const isPartnerSection = block.type.startsWith('sherwin-williams')

  const background = block?.background ?? {}
  const sectionStyles: any = {}
  if (background.color && !isEmbeddedQuote && !isInvoiceTerms) {
    sectionStyles.backgroundColor = background.color
  }
  if (background.textColor) {
    sectionStyles.color = background.textColor
  }

  const backgroundImage = background.image

  if (backgroundImage) {
    sectionStyles.background = `linear-gradient(rgba(0,0,0,${background.overlay}), rgba(0,0,0,${background.overlay})), url(${backgroundImage?.src}) center / cover no-repeat`
  }

  sectionStyles.padding = getBlockPadding({
    noTopPadding:
      sectionIndex === 0 && index === 0 && (block.type === 'banner' || block.type === 'image' || block.type === 'logo'),
    block,
    source: 'presentation',
    size,
    returnDefault: isEmbeddedQuote
  })

  return (
    <div
      id={block.key}
      className={classnames({
        [classes.sectionWrapper]: true,
        [classes.hideSectionOverflow]: !isSettings,
        [classes.pointer]: isSettings && !isPreview && focusedBlock !== block.key,
        [classes.sectionWrapperHovered]: true,
        // the embedded quote renders the standard PaintScout quote UI — the presentation's
        // themed content styles (fonts/typography) must not bleed into it. When useCart is
        // on the quote block is just a themed cart button, which should stay styled.
        // Sherwin-Williams partner sections (#21311) are static SW-branded marketing content
        // reused from v1 — they must always keep their out-of-the-box SW styling regardless
        // of the presentation's selected style.
        [classes.content]: !isEmbeddedQuote && block.type !== 'work-order' && !isPartnerSection,
        [classes.focusBorder]: focusedBlock === block.key
      })}
    >
      <div style={sectionStyles} className={background.useTheme ? 'themeBackground' : null}>
        <Grid
          container
          className={classnames({
            [classes.quoteSectionBorder]: false
          })}
          style={{ height: '100%' }}
        >
          <Grid item xs={12} style={{ height: '100%' }}>
            {children}
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

export default Presentation
